<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'

import BackgroundGradientPanel from '@/components/imockup/inspector/BackgroundGradientPanel.vue'
import type { BackgroundModeId } from '@/stores/mockupEditor'
import { useMockupEditorStore } from '@/stores/mockupEditor'

const store = useMockupEditorStore()
const {
  backgroundHex,
  BG_COLOR_PRESETS,
  backgroundMode,
  cssBackgroundGradient,
  backgroundImageFileName,
  backgroundImageSrc,
} = storeToRefs(store)

const hexForPicker = computed(() => {
  const h = backgroundHex.value.trim()
  if (h.startsWith('#') && h.length >= 7) return h.slice(0, 7)
  return '#f8fafc'
})

const modes = computed<{ id: BackgroundModeId; label: string }[]>(() => [
  { id: 'color', label: store.tUi('inspectorBackground.modeColor') },
  { id: 'gradient', label: store.tUi('inspectorBackground.modeGradient') },
  { id: 'transparent', label: store.tUi('inspectorBackground.modeTransparent') },
  { id: 'image', label: store.tUi('inspectorBackground.modeImage') },
])

function onPick(hex: string): void {
  store.setBackgroundHex(hex)
}

function onMode(e: Event): void {
  const el = e.target as HTMLSelectElement
  store.setBackgroundMode(el.value as BackgroundModeId)
}

const fileInput = ref<HTMLInputElement | null>(null)

function triggerPickImage(): void {
  fileInput.value?.click()
}

function onFileChange(e: Event): void {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) return
  if (!file.type.startsWith('image/')) {
    store.setStubMessage(store.tUi('inspectorBackground.imageInvalidType'))
    return
  }
  const url = URL.createObjectURL(file)
  store.setBackgroundImage(file.name, url)
}

function onClearImage(): void {
  store.clearBackgroundImage()
}
</script>

<template>
  <details class="acc" open>
    <summary class="sum">{{ store.tUi('inspectorBackground.title') }}</summary>
    <div class="body">
      <div class="field">
        <label class="lbl" for="bg-mode">{{ store.tUi('inspectorBackground.type') }}</label>
        <select id="bg-mode" class="sel" :value="backgroundMode" @change="onMode">
          <option v-for="m in modes" :key="m.id" :value="m.id">{{ m.label }}</option>
        </select>
      </div>

      <template v-if="backgroundMode === 'color'">
        <div class="row">
          <label class="sr" for="bg-hex">{{ store.tUi('inspectorBackground.hexColor') }}</label>
          <input id="bg-hex" v-model="backgroundHex" class="hex" type="text" spellcheck="false" />
          <input
            class="picker"
            :value="hexForPicker"
            type="color"
            :aria-label="store.tUi('inspectorBackground.pickBackgroundColor')"
            @input="onPick(($event.target as HTMLInputElement).value)"
          />
        </div>

        <div class="lbl2">{{ store.tUi('inspectorBackground.colorPresets') }}</div>
        <div class="swatches" role="list">
          <button
            v-for="c in BG_COLOR_PRESETS"
            :key="c"
            type="button"
            class="sw"
            :style="{ background: c }"
            :aria-label="store.tUi('inspectorBackground.solidPresetAria', { value: c })"
            :aria-pressed="backgroundHex.toLowerCase().startsWith(c.toLowerCase()) ? 'true' : 'false'"
            role="listitem"
            @click="onPick(c)"
          />
        </div>
      </template>

      <template v-else-if="backgroundMode === 'gradient'">
        <div class="preview" aria-hidden="true" :style="{ background: cssBackgroundGradient }" />
        <BackgroundGradientPanel />
      </template>

      <template v-else-if="backgroundMode === 'transparent'">
        <p class="hint">{{ store.tUi('inspectorBackground.transparentHint') }}</p>
      </template>

      <template v-else>
        <p class="hint">{{ store.tUi('inspectorBackground.imageHint') }}</p>
        <div v-if="backgroundImageSrc" class="img-preview-wrap">
          <img class="img-preview" :src="backgroundImageSrc" alt="background preview" />
          <span class="img-name" :title="backgroundImageFileName ?? ''">
            {{ backgroundImageFileName }}
          </span>
        </div>
        <div class="img-actions">
          <input
            ref="fileInput"
            class="sr"
            type="file"
            accept="image/*"
            @change="onFileChange"
          />
          <button type="button" class="btn primary" @click="triggerPickImage">
            {{
              backgroundImageSrc
                ? store.tUi('inspectorBackground.imageReplace')
                : store.tUi('inspectorBackground.imagePick')
            }}
          </button>
          <button
            v-if="backgroundImageSrc"
            type="button"
            class="btn secondary"
            @click="onClearImage"
          >
            {{ store.tUi('inspectorBackground.imageClear') }}
          </button>
        </div>
      </template>
    </div>
  </details>
</template>

<style scoped>
.acc {
  border: 0;
}

.sum {
  cursor: pointer;
  list-style: none;
  font-size: 0.95rem;
  font-weight: 900;
  letter-spacing: -0.02em;
  color: var(--im-text);
  padding: 0.15rem 0;
}

.sum::-webkit-details-marker {
  display: none;
}

.body {
  display: grid;
  gap: 0.75rem;
  padding-top: 0.65rem;
}

.field {
  display: grid;
  gap: 0.35rem;
}

.lbl,
.lbl2 {
  font-size: 0.75rem;
  font-weight: 800;
  color: var(--im-muted);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.sel {
  width: 100%;
  border-radius: 10px;
  border: 1px solid var(--im-border);
  background: var(--im-surface);
  color: var(--im-text);
  padding: 0.55rem 0.65rem;
  font-size: 0.875rem;
  outline: none;
}

.sel:focus-visible {
  border-color: var(--im-accent);
  box-shadow: 0 0 0 3px var(--im-focus-ring);
}

.preview {
  height: 10px;
  border-radius: 999px;
  border: 1px solid var(--im-border);
}

.row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.5rem;
  align-items: center;
}

.hex {
  width: 100%;
  border-radius: 10px;
  border: 1px solid var(--im-border);
  background: var(--im-surface);
  color: var(--im-text);
  padding: 0.55rem 0.65rem;
  font-size: 0.875rem;
  font-variant-numeric: tabular-nums;
  outline: none;
}

.hex:focus-visible {
  border-color: var(--im-accent);
  box-shadow: 0 0 0 3px var(--im-focus-ring);
}

.picker {
  width: 44px;
  height: 44px;
  padding: 0;
  border-radius: 10px;
  border: 1px solid var(--im-border);
  background: transparent;
  cursor: pointer;
}

.picker:focus-visible {
  outline: 2px solid var(--im-accent);
  outline-offset: 3px;
}

.swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.sw {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  border: 1px solid var(--im-border);
  cursor: pointer;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.35);
  transition: transform 120ms ease;
}

.sw:hover {
  transform: translateY(-1px);
}

.sw:focus-visible {
  outline: 2px solid var(--im-accent);
  outline-offset: 3px;
}

.hint {
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: var(--im-muted);
}

.sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.img-preview-wrap {
  display: grid;
  gap: 0.4rem;
}

.img-preview {
  width: 100%;
  max-height: 110px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid var(--im-border);
  background: var(--im-surface);
}

.img-name {
  font-size: 0.7rem;
  color: var(--im-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.img-actions {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.5rem;
  align-items: center;
}

.btn {
  border-radius: 10px;
  border: 1px solid transparent;
  padding: 0.55rem 0.65rem;
  font-weight: 800;
  font-size: 0.8125rem;
  cursor: pointer;
}

.btn:focus-visible {
  outline: 2px solid var(--im-accent);
  outline-offset: 2px;
}

.btn.primary {
  background: var(--im-interactive-bg);
  color: var(--im-interactive-fg);
  border-color: var(--im-interactive-bg);
}

.btn.primary:hover {
  filter: brightness(1.05);
}

.btn.secondary {
  border-color: var(--im-border);
  background: var(--im-surface);
  color: var(--im-text);
}

.btn.secondary:hover {
  background: var(--im-elevated);
}
</style>
